import {
  Box,
  InputAdornment,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Control,
  Controller,
  FieldError,
  FieldValues,
  Path,
} from 'react-hook-form';
import {
  DatePicker,
  DatePickerProps,
  LocalizationProvider,
} from '@mui/x-date-pickers';
import DateRangeIcon from '../Icons/DateRangeIcon';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import React from 'react';

interface DateInputProps<T extends FieldValues> {
  control: Control<T, any>;
  name: Path<T>;
  label?: string;
  error?: FieldError;
  helperText?: string;
  placeholder?: string;
  onChange?: (value: Dayjs | null) => void;
  disabled?: boolean;
  datePickerProps?: DatePickerProps<Dayjs>;
}

const DateInput = <T extends FieldValues>({
  control,
  name,
  label,
  error,
  helperText,
  placeholder = 'DD.MM.YYYY',
  onChange,
  disabled,
  datePickerProps,
}: DateInputProps<T>) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box display="flex" flexDirection="column" gap="12px" width="100%">
        <Typography variant="caption" padding={'0 4px'} color={'text.primary'}>
          {label}
        </Typography>
        <Box
          display="flex"
          width="100%"
          sx={{
            position: 'relative',
          }}
        >
          <Controller
            control={control}
            name={name}
            render={({ field }) => {
              const handleDateChange = (date: Dayjs | null) => {
                field.onChange(date);
                onChange?.(date);
              };

              return (
                <DatePicker
                  sx={{ width: '100%' }}
                  format="DD.MM.YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  inputRef={field.ref}
                  onChange={handleDateChange}
                  views={['year', 'month', 'day']}
                  openTo={'year'}
                  slots={{
                    openPickerIcon: DateRangeIcon,
                  }}
                  slotProps={{
                    textField: {
                      name: field.name,
                      variant: 'outlined',
                      helperText: helperText || '',
                      error: !!error,
                      placeholder: placeholder,
                      disabled: disabled,
                      InputLabelProps: {
                        shrink: true,
                      },
                      InputProps: isMobile
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <DateRangeIcon />
                              </InputAdornment>
                            ),
                          }
                        : undefined,
                      onInput: (e: React.FormEvent<HTMLInputElement>) => {
                        const value = (e.currentTarget as HTMLInputElement)
                          .value;
                        // Date format validation - no action needed
                        if (value && /\d{2}\.\d{2}\.\d{4}/.test(value)) {
                          // Valid format detected
                        }
                      },
                    },
                  }}
                  disabled={disabled}
                  {...datePickerProps}
                />
              );
            }}
          />
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default DateInput;
